/* ============================================================
 * frontend-v3/src/screens/schedules/ScheduleHealthBadge.tsx
 *
 * NMW-452 — at-a-glance validation indicator for a /schedules list row.
 *
 * Before this, the only way to learn that a programación had a profile
 * mismatch was to open it and press "Validar". The list gave no signal at
 * all, so a broken playlist looked exactly like a healthy one.
 *
 * This badge binds the lean per-schedule projection of the SAME rule engine
 * the drawer uses:
 *
 *   api.compliance.scheduleHealth   → counts + topReason   (this badge)
 *   api.compliance.checkSchedule    → full rule bodies     (ScheduleValidationDrawer)
 *
 * One engine, two projections — the number here and the drawer's list can
 * never disagree. The tone/icon vocabulary is imported from the drawer
 * (`TONE_FOR_STATUS`) rather than re-declared, so the list and the detail
 * surface speak one visual language.
 *
 * WHAT THIS PILL MEANS (and what it does not):
 * It reports the schedule VALIDATION output — profile consistency, ad-time
 * ratio, empty playlist. It is NOT asset health. A schedule whose items point
 * at deleted assets renders "Sin problemas" today, because the backend rule
 * set silently skips a missing asset. That gap is the kernel's to close
 * (convex/lib/scheduleCompliance.ts); when it does, this badge inherits the
 * new rule for free. Until then the tooltip says so, out loud.
 *
 * Honest-state contract, in precedence order:
 *   not queried (past the cap / malformed id) → neutral "Sin verificar"
 *   undefined (loading)                       → Skeleton, never a green pill
 *   null (schedule vanished)                  → neutral "No disponible"
 *   status !== 'ok' (too large to evaluate)   → neutral "Sin verificar"
 *   warnings/failures                         → warn/bad "N avisos"
 *   clean                                     → good "Sin problemas"
 * A fabricated "all good" is impossible: every green pill is a real backend
 * result that counted zero rules. A backend throw is NOT reported as health
 * either: it is caught by this module's OWN error boundary and degrades to the
 * neutral "Sin verificar" pill (plus a console.error). That boundary is load-
 * bearing, not decoration — `useQuery` re-throws Convex errors during render,
 * and the only other boundary in this app is `AppErrorBoundary` at the root,
 * so without it one fat schedule (a read-budget overrun in `scheduleHealth`)
 * among the up-to-50 auto-mounted badges would replace the entire console with
 * the app-level 500 screen. The blast radius is one badge, by construction.
 *
 * Query scope: one subscription per rendered row, capped at
 * HEALTH_BADGE_LIMIT. Per-row (rather than one batched query over the list)
 * because each Convex query gets its own 16 MiB read budget and its own
 * cache entry — a batched variant would share one byte budget across N
 * schedules and re-run wholesale on any single write. See the module header
 * of convex/lib/scheduleCompliance.ts for the full argument.
 * ============================================================ */
import { Component, type ReactNode } from 'react';
import { useQuery } from 'convex/react';
import { api } from '@convex/api';
import type { Id } from '@convex/dataModel';
import { Pill, Skeleton } from '../../components/primitives';
import { TONE_FOR_STATUS } from '../schedule/ScheduleValidationDrawer';
import { navigate } from '../../lib/nav';

/**
 * Rows past this index render an honest "Sin verificar" neutral pill instead
 * of subscribing. Bounds the websocket subscription count regardless of how
 * large the schedules table grows. The real fix is paginating /schedules —
 * when that lands, replace this with the page size.
 */
export const HEALTH_BADGE_LIMIT = 50;

/** The `scheduleHealth` projection, as this badge consumes it. */
export interface ScheduleHealth {
  status: 'ok' | 'unavailable';
  itemCount: number;
  overallStatus: 'pass' | 'warning' | 'fail';
  warningCount: number;
  failedCount: number;
  byType: {
    profile_consistency: number;
    total_ad_time: number;
    scte35_format: number;
  };
  topReason: string | null;
}

export interface ScheduleHealthBadgeProps {
  scheduleId: string;
  /** False past HEALTH_BADGE_LIMIT — renders the neutral pill, no query. */
  enabled?: boolean;
}

/** Spanish singular/plural label per rule type, for the tooltip breakdown. */
const TYPE_LABEL: Record<
  keyof ScheduleHealth['byType'],
  [singular: string, plural: string]
> = {
  profile_consistency: [
    'perfil de video inconsistente',
    'perfiles de video inconsistentes',
  ],
  total_ad_time: ['exceso de anuncios', 'excesos de anuncios'],
  scte35_format: [
    'observación de estructura',
    'observaciones de estructura',
  ],
};

const SCOPE_NOTE =
  'Refleja la validación de la programación, no el estado de los recursos.';

/**
 * Builds the hover text: total, the per-type split (so the number here is
 * reconcilable with the editor's "Validar (n)", which counts profile
 * warnings only), the first rule's own words, and the scope note.
 */
export function buildHealthTitle(health: ScheduleHealth): string {
  const total = health.warningCount + health.failedCount;
  const parts: string[] = [total === 1 ? '1 aviso' : `${total} avisos`];
  for (const key of Object.keys(TYPE_LABEL) as Array<
    keyof ScheduleHealth['byType']
  >) {
    const n = health.byType[key];
    if (n === 0) continue;
    // The kernel files the empty-playlist rule under `scte35_format` (it has
    // no bucket of its own), so on a zero-item schedule that count is not a
    // structural SCTE observation — say what it actually is.
    if (key === 'scte35_format' && health.itemCount === 0) {
      parts.push('programación vacía');
      continue;
    }
    parts.push(`${n} ${TYPE_LABEL[key][n === 1 ? 0 : 1]}`);
  }
  const breakdown = parts.join(' · ');
  const reason = health.topReason ? `\n${health.topReason}` : '';
  return `${breakdown}${reason}\n${SCOPE_NOTE}`;
}

/** The neutral "not evaluated" pill, shared by the skip / unavailable / caught-throw paths. */
function UnverifiedPill({ title }: { title: string }) {
  return (
    <span className="circa-health-slot" title={title}>
      <Pill tone="neutral" size="sm">
        Sin verificar
      </Pill>
    </span>
  );
}

/**
 * Per-badge error boundary. `useQuery` re-throws a Convex query error during
 * render; the nearest boundary in this app is the root `AppErrorBoundary`,
 * which blanks the whole console. One row's health check failing (a read-budget
 * overrun on a fat schedule, a mid-deploy blip) must cost that row's pill and
 * nothing else — so it is caught here and degrades to the honest neutral pill.
 * Never renders a green check: a caught throw is "not verified", not "clean".
 */
class ScheduleHealthBadgeBoundary extends Component<
  { children: ReactNode },
  { failed: boolean }
> {
  state = { failed: false };

  static getDerivedStateFromError() {
    return { failed: true };
  }

  componentDidCatch(error: unknown) {
    // Keys, not values: the schedule id is already in the row; log the cause.
    console.error('[ScheduleHealthBadge] health query failed', error);
  }

  render() {
    if (this.state.failed) {
      return (
        <UnverifiedPill
          title={`No se pudo validar la programación. ${SCOPE_NOTE}`}
        />
      );
    }
    return this.props.children;
  }
}

export function ScheduleHealthBadge(props: ScheduleHealthBadgeProps) {
  return (
    // `key` resets the boundary when the row is reused for another schedule,
    // so a failure never sticks to an unrelated programación.
    <ScheduleHealthBadgeBoundary key={props.scheduleId}>
      <ScheduleHealthBadgeInner {...props} />
    </ScheduleHealthBadgeBoundary>
  );
}

function ScheduleHealthBadgeInner({
  scheduleId,
  enabled = true,
}: ScheduleHealthBadgeProps) {
  // Same guard as FormatMismatchCell / SchedulesEditor: never let a non-id
  // (the `__live__` sentinel, a hand-typed URL) past the Convex arg boundary,
  // where it would throw an ArgumentValidationError past the error boundary.
  const validId = /^[a-z0-9]{20,}$/.test(scheduleId);
  const shouldQuery = enabled && validId;

  const health = useQuery(
    api.compliance.scheduleHealth,
    shouldQuery ? { scheduleId: scheduleId as unknown as Id<'schedules'> } : 'skip',
  ) as ScheduleHealth | null | undefined;

  // 1 — not queried. Explicitly NOT a green check.
  if (!shouldQuery) {
    return (
      <UnverifiedPill
        title={`Validación no ejecutada en esta fila. ${SCOPE_NOTE}`}
      />
    );
  }

  // 2 — loading. A skeleton, never an optimistic value.
  if (health === undefined) {
    return (
      <span className="circa-health-slot">
        <Skeleton w={92} h={18} r={999} aria-busy="true" />
      </span>
    );
  }

  // 3 — the schedule vanished between the list read and this one.
  if (health === null) {
    return (
      <span
        className="circa-health-slot"
        title="La programación ya no existe."
      >
        <Pill tone="neutral" size="sm">
          No disponible
        </Pill>
      </span>
    );
  }

  // 4 — the backend declined to evaluate (too many items). Honest neutral.
  if (health.status !== 'ok') {
    return (
      <UnverifiedPill
        title={`${health.topReason ?? 'No se pudo validar la programación.'}\n${SCOPE_NOTE}`}
      />
    );
  }

  const total = health.warningCount + health.failedCount;

  // 5 — clean.
  if (total === 0) {
    return (
      <span className="circa-health-slot" title={`Sin observaciones. ${SCOPE_NOTE}`}>
        <Pill tone={TONE_FOR_STATUS.pass} size="sm" icon="checkCircle">
          Sin problemas
        </Pill>
      </span>
    );
  }

  // 6 — warnings / failures. Clicking lands the operator straight in the
  // validation drawer of the edit page (`?validate=1`), which is the only
  // surface that can act on these. stopPropagation is deliberate: without it
  // the row's own navigate would bubble afterwards and drop the query param.
  const failed = health.failedCount > 0;
  return (
    <span
      className="circa-health-slot"
      title={buildHealthTitle(health)}
      role="button"
      tabIndex={0}
      onClick={(e) => {
        e.stopPropagation();
        navigate(`/schedules/${scheduleId}/edit?validate=1`);
      }}
      onKeyDown={(e) => {
        if (e.key !== 'Enter' && e.key !== ' ') return;
        e.preventDefault();
        e.stopPropagation();
        navigate(`/schedules/${scheduleId}/edit?validate=1`);
      }}
    >
      <Pill
        tone={failed ? TONE_FOR_STATUS.fail : TONE_FOR_STATUS.warning}
        size="sm"
        icon={failed ? 'alertCircle' : 'alertTriangle'}
      >
        {total === 1 ? '1 aviso' : `${total} avisos`}
      </Pill>
    </span>
  );
}

export default ScheduleHealthBadge;
